import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../hooks/useApi';
import Icon from '../components/Icon';
import './ClientsList.css';

const FORM_VIDE = {
  entreprise: '', contact_prenom: '', contact_nom: '', contact_email: '',
  contact_tel: '', adresse_facturation: '', ville: '', code_postal: '',
  province: 'QC', numero_po: '', tps_exempt: false, tvq_exempt: false, notes: '',
};

// ── Skeleton ────────────────────────────────────────────────────────────────

function ClientsListSkeleton() {
  return (
    <div className="clients-page">
      <div className="clients-header">
        <div>
          <div className="skeleton" style={{ height: 36, width: 260, marginBottom: 8 }} />
          <div className="skeleton" style={{ height: 12, width: 120 }} />
        </div>
        <div className="skeleton" style={{ height: 36, width: 140, borderRadius: 'var(--radius-md)' }} />
      </div>
      <div className="card clients-filtres">
        <div className="skeleton" style={{ flex: 1, height: 36 }} />
        <div className="skeleton" style={{ height: 36, width: 36, borderRadius: 'var(--radius-md)' }} />
      </div>
      <div className="card table-wrapper">
        <table className="esc-table">
          <thead>
            <tr>
              {['Entreprise','Contact','Courriel','Téléphone','Contrats','Taxes','Actions'].map(h => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[0,1,2,3].map(i => (
              <tr key={i}>
                {[140,120,180,100,70,80,80].map((w, j) => (
                  <td key={j}><div className="skeleton" style={{ height: 14, width: w }} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ClientsList() {
  const navigate = useNavigate();
  const [clients, setClients]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [erreur,  setErreur]      = useState('');
  const [recherche, setRecherche] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [form, setForm]           = useState(FORM_VIDE);
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState('');

  const charger = useCallback(async () => {
    try {
      setLoading(true);
      setErreur('');
      const data = await api.get('/corpo/clients');
      setClients(Array.isArray(data) ? data : []);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  function ouvrirModal(client = null) {
    setEditClient(client);
    if (client) {
      setForm({
        entreprise:          client.entreprise || '',
        contact_prenom:      client.contact_prenom || '',
        contact_nom:         client.contact_nom || '',
        contact_email:       client.contact_email || '',
        contact_tel:         client.contact_tel || '',
        adresse_facturation: client.adresse_facturation || '',
        ville:               client.ville || '',
        code_postal:         client.code_postal || '',
        province:            client.province || 'QC',
        numero_po:           client.numero_po || '',
        tps_exempt:          !!client.tps_exempt,
        tvq_exempt:          !!client.tvq_exempt,
        notes:               client.notes || '',
      });
    } else {
      setForm(FORM_VIDE);
    }
    setShowModal(true);
  }

  async function sauvegarder(e) {
    e.preventDefault();
    try {
      setSaving(true);
      if (editClient) {
        await api.put(`/corpo/clients/${editClient.id}`, form);
        showMsg('Client modifié');
      } else {
        await api.post('/corpo/clients', form);
        showMsg('Client créé');
      }
      await charger();
      setShowModal(false);
    } catch (e) {
      alert('Erreur : ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function supprimer(client) {
    if (!window.confirm(`Supprimer le client "${client.entreprise}" ?`)) return;
    try {
      await api.delete(`/corpo/clients/${client.id}`);
      showMsg('Client supprimé');
      await charger();
    } catch (e) {
      alert(e.response?.data?.error || 'Impossible de supprimer le client');
    }
  }

  function showMsg(m) {
    setMsg(m);
    setTimeout(() => setMsg(''), 3000);
  }

  const filtered = clients.filter(c => {
    const q = recherche.toLowerCase();
    return (
      c.entreprise.toLowerCase().includes(q) ||
      c.contact_prenom.toLowerCase().includes(q) ||
      c.contact_nom.toLowerCase().includes(q) ||
      c.contact_email.toLowerCase().includes(q)
    );
  });

  if (loading) return <ClientsListSkeleton />;

  return (
    <div className="clients-page">
      {msg && <div className="flash-msg">{msg}</div>}

      {/* En-tête */}
      <div className="clients-header">
        <div>
          <h1 className="page-title">Clients Corporatifs</h1>
          <p className="page-subtitle muted">{clients.length} client{clients.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn btn-primary" onClick={() => ouvrirModal()}>
          <Icon name="ajouter" size={15} />
          Nouveau client
        </button>
      </div>

      {/* Barre recherche */}
      <div className="clients-filtres card">
        <input
          type="text"
          className="recherche-input"
          placeholder="Rechercher par entreprise, contact, courriel…"
          value={recherche}
          onChange={e => setRecherche(e.target.value)}
        />
        <button className="btn btn-ghost" onClick={charger} title="Actualiser">
          <Icon name="rafraichir" size={15} />
        </button>
      </div>

      {/* Erreur */}
      {erreur && (
        <div className="error-inline" style={{ marginBottom: 'var(--space-4)' }}>
          <Icon name="erreur" size={16} />
          {erreur}
        </div>
      )}

      {/* Tableau / État vide */}
      {filtered.length === 0 ? (
        <div className="empty-state card">
          <Icon name="entreprise" size={40} />
          <p className="empty-state__titre">
            {recherche ? 'Aucun résultat trouvé' : 'Aucun client enregistré'}
          </p>
          {!recherche && (
            <button className="btn btn-primary" onClick={() => ouvrirModal()}>
              <Icon name="ajouter" size={15} />
              Créer un client
            </button>
          )}
        </div>
      ) : (
        <div className="card table-wrapper rise-in">
          <table className="esc-table">
            <thead>
              <tr>
                <th>Entreprise</th>
                <th>Contact</th>
                <th>Courriel</th>
                <th>Téléphone</th>
                <th>Contrats</th>
                <th>Taxes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id}>
                  <td className="entreprise-cell">
                    <span className="entreprise-name">{c.entreprise}</span>
                    {c.numero_po && <span className="muted" style={{ fontSize: 11 }}>PO: {c.numero_po}</span>}
                  </td>
                  <td>{c.contact_prenom} {c.contact_nom}</td>
                  <td>
                    <a href={`mailto:${c.contact_email}`} className="email-link">{c.contact_email}</a>
                  </td>
                  <td className="muted">{c.contact_tel || '—'}</td>
                  <td className="centered">
                    {c.nb_contrats > 0 ? (
                      <button
                        className="btn btn-ghost btn-xs"
                        onClick={() => navigate(`/corpo?client_id=${c.id}`)}
                      >
                        {c.nb_contrats} contrat{c.nb_contrats !== 1 ? 's' : ''}
                      </button>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    <div className="taxes-badges">
                      {c.tps_exempt && (
                        <span className="badge-tax exempt">
                          <Icon name="check" size={10} />
                          TPS
                        </span>
                      )}
                      {c.tvq_exempt && (
                        <span className="badge-tax exempt">
                          <Icon name="check" size={10} />
                          TVQ
                        </span>
                      )}
                      {!c.tps_exempt && !c.tvq_exempt && (
                        <span className="muted" style={{ fontSize: 11 }}>Standard</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="row-actions">
                      <button
                        className="btn btn-ghost btn-xs"
                        onClick={() => ouvrirModal(c)}
                        title="Modifier"
                      >
                        <Icon name="modifier" size={13} />
                      </button>
                      <button
                        className="btn btn-danger btn-xs"
                        onClick={() => supprimer(c)}
                        disabled={c.nb_contrats > 0}
                        title={c.nb_contrats > 0 ? 'Client avec contrats actifs' : 'Supprimer'}
                      >
                        <Icon name="supprimer" size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal client */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editClient ? 'Modifier le client' : 'Nouveau client'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <Icon name="fermer" size={16} />
              </button>
            </div>
            <form onSubmit={sauvegarder} className="modal-form">
              <div className="form-group">
                <label>Entreprise *</label>
                <input type="text" value={form.entreprise}
                  onChange={e => setForm(f => ({ ...f, entreprise: e.target.value }))}
                  required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Prénom *</label>
                  <input type="text" value={form.contact_prenom}
                    onChange={e => setForm(f => ({ ...f, contact_prenom: e.target.value }))}
                    required />
                </div>
                <div className="form-group">
                  <label>Nom *</label>
                  <input type="text" value={form.contact_nom}
                    onChange={e => setForm(f => ({ ...f, contact_nom: e.target.value }))}
                    required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Courriel *</label>
                  <input type="email" value={form.contact_email}
                    onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))}
                    required />
                </div>
                <div className="form-group">
                  <label>Téléphone</label>
                  <input type="tel" value={form.contact_tel}
                    onChange={e => setForm(f => ({ ...f, contact_tel: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label>Adresse de facturation</label>
                <input type="text" value={form.adresse_facturation}
                  onChange={e => setForm(f => ({ ...f, adresse_facturation: e.target.value }))} />
              </div>
              <div className="form-row-3">
                <div className="form-group">
                  <label>Ville</label>
                  <input type="text" value={form.ville}
                    onChange={e => setForm(f => ({ ...f, ville: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Code postal</label>
                  <input type="text" value={form.code_postal}
                    onChange={e => setForm(f => ({ ...f, code_postal: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Province</label>
                  <select value={form.province}
                    onChange={e => setForm(f => ({ ...f, province: e.target.value }))}>
                    <option>QC</option><option>ON</option><option>BC</option>
                    <option>AB</option><option>MB</option><option>SK</option>
                    <option>NB</option><option>NS</option><option>PE</option>
                    <option>NL</option><option>NT</option><option>YT</option><option>NU</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Numéro de PO (optionnel)</label>
                <input type="text" value={form.numero_po}
                  onChange={e => setForm(f => ({ ...f, numero_po: e.target.value }))} />
              </div>
              <div className="form-row">
                <label className="checkbox-label">
                  <input type="checkbox" checked={form.tps_exempt}
                    onChange={e => setForm(f => ({ ...f, tps_exempt: e.target.checked }))} />
                  Exempt de TPS
                </label>
                <label className="checkbox-label">
                  <input type="checkbox" checked={form.tvq_exempt}
                    onChange={e => setForm(f => ({ ...f, tvq_exempt: e.target.checked }))} />
                  Exempt de TVQ
                </label>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea rows={3} value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Sauvegarde…' : (editClient ? 'Modifier' : 'Créer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
